<template>
  <div class="pa-4">
    <breadcrumb />

    <div class="flex-wrap mb-4 d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <h2 class="mr-3 black--text">{{ $t('reports.anomalyDetection.title') }}</h2>
        <v-btn small rounded color="primary" depressed @click="showGuide = true">
          <v-icon left small>mdi-help-circle-outline</v-icon>
          {{ $t('reports.anomalyDetection.aiReportGuide') }}
        </v-btn>
      </div>
      <div>
        <v-btn outlined color="primary" class="mr-2" @click="cancel">
          {{ $t('reports.anomalyDetection.cancel') }}
        </v-btn>
        <v-btn color="primary" depressed :disabled="!canGenerate" :loading="generating" @click="generate">
          {{ $t('reports.anomalyDetection.generateReport') }}
        </v-btn>
      </div>
    </div>

    <v-card class="pa-4" outlined>
      <v-row>
        <!-- Map -->
        <v-col cols="12" md="8">
          <h3 class="mb-1 black--text">{{ $t('reports.anomalyDetection.selectFarmGeofence') }}</h3>
          <p class="mb-3 grey--text">{{ $t('reports.anomalyDetection.selectFarmGeofenceHelp') }}</p>
          <geofence-map
            :source="source"
            :zone="selectedZone"
            :geojson-coordinates="geojsonCoords"
            :radius-meters="circleRadius"
            @update:geometry="onDrawGeometry"
          />
        </v-col>

        <!-- Right panel -->
        <v-col cols="12" md="4">
          <v-btn-toggle v-model="source" mandatory dense class="flex-wrap mb-4 d-flex">
            <v-btn value="farm_zone">{{ $t('reports.anomalyDetection.sourceFarmZone') }}</v-btn>
            <v-btn value="polygon">{{ $t('reports.anomalyDetection.sourcePolygon') }}</v-btn>
            <v-btn value="circle">{{ $t('reports.anomalyDetection.sourceCircle') }}</v-btn>
            <v-btn value="geojson">{{ $t('reports.anomalyDetection.sourceGeojson') }}</v-btn>
          </v-btn-toggle>

          <div v-if="source === 'farm_zone'">
            <label class="mb-1 d-block black--text">{{ $t('reports.anomalyDetection.selectFarmZone') }}</label>
            <farm-zone-select v-model="selectedZone" />
          </div>

          <div v-else-if="source === 'polygon'">
            <v-alert dense text type="info" class="mb-0">
              {{ $t('reports.anomalyDetection.drawPolygon') }} — {{ $t('reports.anomalyDetection.drawPolygonHelp') }}
            </v-alert>
          </div>
          <div v-else-if="source === 'circle'">
            <v-alert dense text type="info" class="mb-3">
              {{ $t('reports.anomalyDetection.drawCircle') }} — {{ $t('reports.anomalyDetection.drawCircleHelp') }}
            </v-alert>
            <v-text-field
              v-model.number="circleRadius"
              :label="$t('reports.anomalyDetection.radiusM')"
              type="number"
              min="1"
              suffix="m"
              outlined
              dense
              hide-details
            />
          </div>

          <div v-else-if="source === 'geojson'">
            <h4 class="mb-1 primary--text">{{ $t('reports.anomalyDetection.uploadGeojsonFile') }}</h4>
            <p class="mb-2 grey--text text-caption">{{ $t('reports.anomalyDetection.uploadGeojsonHelp') }}</p>
            <v-file-input
              accept=".geojson,.json"
              :label="$t('reports.anomalyDetection.selectAFile')"
              prepend-icon="mdi-upload"
              outlined
              dense
              hide-details
              :loading="uploadingGeojson"
              @change="onGeojsonUpload"
            />
          </div>

          <v-divider class="my-4" />

          <!-- Detect anomalies: start date + number of historical reports -->
          <h4 class="mb-1 primary--text">{{ $t('reports.anomalyDetection.detectAnomalies') }}</h4>
          <p class="mb-3 grey--text text-caption">{{ $t('reports.anomalyDetection.detectAnomaliesHelp') }}</p>

          <label class="mb-1 d-block black--text">{{ $t('reports.anomalyDetection.startDate') }}</label>
          <div v-if="datesLoading" class="mb-2 grey--text">
            <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-2" />
            {{ $t('reports.anomalyDetection.fetchingDates') }}
          </div>
          <v-select
            v-else
            v-model="selectedDate"
            :items="dateItems"
            :placeholder="$t('reports.anomalyDetection.select')"
            :no-data-text="
              hasGeofence
                ? $t('reports.anomalyDetection.noAvailableDates')
                : $t('reports.anomalyDetection.selectDateToGenerate')
            "
            outlined
            dense
            hide-details
          />

          <label class="mt-4 mb-1 d-block black--text">{{
            $t('reports.anomalyDetection.numberOfHistoricalReports')
          }}</label>
          <v-text-field
            v-model.number="historicalReports"
            :placeholder="$t('reports.anomalyDetection.historicalReportsPlaceholder')"
            type="number"
            min="0"
            max="36"
            outlined
            dense
            hide-details
          />
          <v-alert dense text color="warning" class="mt-2 mb-0 note-alert">
            <span class="text-caption">
              <strong>{{ $t('reports.anomalyDetection.historicalReportsNote') }}</strong>
            </span>
          </v-alert>
        </v-col>
      </v-row>
      <!-- Estimated compute cost — charges for the primary + N historical reports -->
      <estimated-compute-cost report-type="anomaly_detection" :geofence="geofencePayload" :units="unitsCount" />
    </v-card>

    <guide-dialog :value="showGuide" @input="showGuide = $event" />
  </div>
</template>

<script>
import AnomalyDetectionService from '@/_services/AnomalyDetectionService';
import S3UploadService from '@/_services/S3UploadService';
// Reuse the geofence map + farm/zone selector from Crop Progression.
import GeofenceMap from '../crop-progression/components/GeofenceMap.vue';
import FarmZoneSelect from '../crop-progression/components/FarmZoneSelect.vue';
import GuideDialog from './components/GuideDialog.vue';
import EstimatedComputeCost from '../shared/EstimatedComputeCost.vue';
import reportError from '@/mixins/reportError';

const DEFAULT_CIRCLE_RADIUS_M = 20; // meters
const DEFAULT_HISTORICAL_REPORTS = 0; // 0 -> only the selected date

export default {
  name: 'AnomalyDetectionCreate',
  components: { GeofenceMap, FarmZoneSelect, GuideDialog, EstimatedComputeCost },
  mixins: [reportError],
  data() {
    return {
      source: 'farm_zone',
      selectedZone: null,
      circleRadius: DEFAULT_CIRCLE_RADIUS_M,
      drawnGeometry: null,
      geojsonCoords: [],
      geojsonS3Path: null,
      uploadingGeojson: false,
      dates: [],
      datesLoading: false,
      selectedDate: null,
      historicalReports: DEFAULT_HISTORICAL_REPORTS,
      generating: false,
      showGuide: false,
      datesTimer: null,
    };
  },
  computed: {
    geofencePayload() {
      switch (this.source) {
        case 'farm_zone':
          return this.selectedZone
            ? {
                geofenceType: 'farm_zone',
                geofenceId: this.selectedZone.geofenceId,
                farmId: this.selectedZone.farmId,
              }
            : null;
        case 'polygon':
          return this.drawnGeometry && this.drawnGeometry.shape === 'polygon'
            ? { geofenceType: 'polygon', coordinates: this.drawnGeometry.coordinates }
            : null;
        case 'circle':
          return this.drawnGeometry && this.drawnGeometry.shape === 'circle'
            ? {
                geofenceType: 'circle',
                latitude: this.drawnGeometry.latitude,
                longitude: this.drawnGeometry.longitude,
                radius: this.drawnGeometry.radius,
              }
            : null;
        case 'geojson':
          return this.geojsonCoords.length >= 3
            ? {
                geofenceType: 'geojson',
                coordinates: this.geojsonCoords,
                geojsonS3Path: this.geojsonS3Path,
              }
            : null;
        default:
          return null;
      }
    },
    hasGeofence() {
      return !!this.geofencePayload;
    },
    dateItems() {
      return (this.dates || []).map((d) => ({
        text: this.$options.filters.formatDate(d),
        value: d,
      }));
    },
    validHistoricalReports() {
      // Empty is treated as 0 (only the selected date).
      if (this.historicalReports === '' || this.historicalReports == null) return true;
      const n = parseInt(this.historicalReports, 10);
      return Number.isInteger(n) && n >= 0 && n <= 36;
    },
    // Reports this action generates: the selected date + N historical siblings.
    unitsCount() {
      const n = parseInt(this.historicalReports, 10);
      return (Number.isInteger(n) && n >= 0 ? n : 0) + 1;
    },
    canGenerate() {
      return this.hasGeofence && !!this.selectedDate && this.validHistoricalReports && !this.generating;
    },
  },
  watch: {
    source() {
      this.drawnGeometry = null;
      if (this.source !== 'geojson') {
        this.geojsonCoords = [];
        this.geojsonS3Path = null;
      }
      if (this.source !== 'farm_zone') this.selectedZone = null;
      if (this.source === 'circle') this.circleRadius = DEFAULT_CIRCLE_RADIUS_M;
      this.resetDates();
    },
    geofencePayload() {
      this.scheduleFetchDates();
    },
  },
  methods: {
    onDrawGeometry(geom) {
      this.drawnGeometry = geom;
      if (geom && geom.shape === 'circle' && geom.radius != null) {
        const meters = Math.round(geom.radius * 1000);
        if (Math.abs(meters - Number(this.circleRadius)) >= 1) {
          this.circleRadius = meters;
        }
      }
    },
    resetDates() {
      this.dates = [];
      this.selectedDate = null;
    },
    scheduleFetchDates() {
      this.resetDates();
      clearTimeout(this.datesTimer);
      if (!this.geofencePayload) return;
      this.datesTimer = setTimeout(this.fetchDates, 400);
    },
    async fetchDates() {
      if (!this.geofencePayload) return;
      this.datesLoading = true;
      try {
        const env = await AnomalyDetectionService.getAvailableDates(this.geofencePayload);
        this.dates = (env && env.data && env.data.dates) || [];
      } catch (e) {
        this.dates = [];
        this.$notify({
          text: this.$t('reports.anomalyDetection.datesFailed'),
          type: 'error',
        });
      } finally {
        this.datesLoading = false;
      }
    },
    async onGeojsonUpload(file) {
      if (!file) {
        this.geojsonCoords = [];
        this.geojsonS3Path = null;
        return;
      }
      const text = await file.text().catch(() => null);
      if (!text) return;
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        this.$notify({
          title: this.$t('reports.anomalyDetection.invalidGeojsonFile'),
          text: this.$t('reports.anomalyDetection.invalidGeojsonFileDescription'),
          type: 'error',
        });
        return;
      }
      const coords = this.extractPolygonFromGeojson(parsed);
      if (coords.length < 3) {
        this.$notify({
          title: this.$t('reports.anomalyDetection.invalidGeojsonFile'),
          text: this.$t('reports.anomalyDetection.invalidGeojsonFileDescription'),
          type: 'error',
        });
        return;
      }
      this.geojsonCoords = coords;
      this.uploadingGeojson = true;
      try {
        const { key } = await S3UploadService.singleUpload(file, 'anomaly-detection/geojson');
        this.geojsonS3Path = key;
      } catch (e) {
        this.$notify({
          text: this.$t('reports.anomalyDetection.uploadFailed'),
          type: 'error',
        });
      } finally {
        this.uploadingGeojson = false;
      }
    },
    extractPolygonFromGeojson(geojson) {
      const ringToLatLng = (ring) => ring.map((c) => ({ lat: c[1], lng: c[0] }));
      try {
        if (geojson.type === 'FeatureCollection' && geojson.features && geojson.features.length) {
          return this.extractPolygonFromGeojson(geojson.features[0]);
        }
        const geom = geojson.type === 'Feature' ? geojson.geometry : geojson;
        if (!geom) return [];
        if (geom.type === 'Polygon') return ringToLatLng(geom.coordinates[0]);
        if (geom.type === 'MultiPolygon') return ringToLatLng(geom.coordinates[0][0]);
      } catch (e) {
        return [];
      }
      return [];
    },
    async generate() {
      if (!this.canGenerate) return;
      this.generating = true;
      try {
        const historical = parseInt(this.historicalReports, 10);
        const payload = {
          ...this.geofencePayload,
          selectedDate: this.selectedDate,
          historicalReports: Number.isInteger(historical) ? historical : 0,
        };
        const env = await AnomalyDetectionService.generateReport(payload);
        const report = env && env.data && env.data.report;
        if (report && report.reportUid) {
          this.$router.push({
            name: 'anomalyDetectionView',
            params: { reportUid: report.reportUid },
          });
        } else {
          this.$notify({
            text: this.reportErrorMessage({ data: env }, 'reports.anomalyDetection.generateFailed'),
            type: 'error',
          });
        }
      } catch (e) {
        this.$notify({
          text: this.reportErrorMessage(e, 'reports.anomalyDetection.generateFailed'),
          type: 'error',
        });
      } finally {
        this.generating = false;
      }
    },
    cancel() {
      this.$router.push({ name: 'anomalyDetectionList' });
    },
  },
};
</script>

<style scoped>
.compute-card {
  background: #f4fbf7;
}
.note-alert {
  border: 1px solid #ffe1a6;
}
</style>
